import { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { Check, ChevronDown, X } from "lucide-react";

// Searchable multi-select of existing Customers / Builders. value = [customer_id]
export const CustomerMultiSelect = ({ customers, value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const sel = value || [];
  const byId = Object.fromEntries(customers.map((c) => [c.id, c]));
  const toggle = (id) => onChange(sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  return (
    <div data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={`${testId}-trigger`}
            className="w-full min-h-9 flex items-center gap-1.5 flex-wrap border border-slate-200 rounded-md px-2 py-1 text-sm text-left hover:border-navy transition-colors">
            {sel.length === 0 && <span className="text-slate-400 px-1">Select Builder / Customer…</span>}
            {sel.map((id) => (
              <span key={id} className="inline-flex items-center gap-1 bg-navy/10 text-navy rounded-full px-2 py-0.5 text-xs font-medium" data-testid={`${testId}-chip-${id}`}>
                {byId[id]?.name || "Deleted customer"}
                <X className="w-3 h-3 cursor-pointer hover:text-red-500" onClick={(e) => { e.stopPropagation(); toggle(id); }} />
              </span>
            ))}
            <ChevronDown className="w-4 h-4 text-slate-400 ml-auto shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-72" align="start">
          <Command>
            <CommandInput placeholder="Search customers…" data-testid={`${testId}-search`} />
            <CommandList>
              <CommandEmpty>No customer found.</CommandEmpty>
              {customers.map((c) => (
                <CommandItem key={c.id} value={`${c.name} ${c.id}`} onSelect={() => toggle(c.id)} data-testid={`${testId}-option-${c.id}`}>
                  <Check className={`w-4 h-4 mr-2 ${sel.includes(c.id) ? "opacity-100" : "opacity-0"}`} />
                  <span className="flex-1 truncate">{c.name}</span>
                  {c.customer_type && <span className="text-[10px] uppercase text-slate-400">{c.customer_type}</span>}
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Builder assignment block shown above a Price List group name.
export const GroupBuilderAssign = ({ customers, ids, alsoStandard, onIds, onAlsoStandard, testId }) => (
  <div className="mb-3" data-testid={testId}>
    <p className="text-xs font-medium text-slate-500 mb-1">Customer / Builder</p>
    <CustomerMultiSelect customers={customers} value={ids} onChange={onIds} testId={`${testId}-select`} />
    {(ids || []).length > 0 ? (
      <label className="flex items-center gap-1.5 mt-1.5 text-[11px] text-slate-500 cursor-pointer">
        <input type="checkbox" checked={!!alsoStandard} onChange={(e) => onAlsoStandard(e.target.checked)} data-testid={`${testId}-also-standard`} />
        Also show to customers without special rates (standard)
      </label>
    ) : (
      <p className="text-[11px] text-slate-400 mt-1">No builder selected: standard rates, shown to customers without special rates.</p>
    )}
  </div>
);
